import React, { useCallback, useRef } from 'react'
import { useToggle } from 'react-use'
import type { WorkOrder } from '@ztechapps/common.contract'
import type { BodyRowConfig } from '@ztechapps/webui.data-tools'
import { useTableBody } from '@ztechapps/webui.data-tools'
import { useWorkOrderChildrenList } from '@ztechapps/work-order.common'
import { ShowIf, Spinner } from '@ztechapps/webui.util'
import { ListRow } from './ListRow'

type ExpandableWorkOrderRowProps = {
  listItem: WorkOrder
  index: number
  level: number
  searchValue?: string
}

export const ExpandableWorkOrderRow = ({ listItem, index, level, searchValue }: ExpandableWorkOrderRowProps) => {
  const [isExpanded, toggleExpanded] = useToggle(false)
  const { list, loading, executeQuery } = useWorkOrderChildrenList()
  const lastSearchRef = useRef<string | undefined>(undefined)

  const { onRowClick, onRowDoubleClick, highlightClassName, renderDataBodyCell, key } = useTableBody(listItem, index)

  const handleToggleExpand = useCallback(() => {
    const nextExpanded = !isExpanded
    toggleExpanded()

    if (nextExpanded) {
      const searchChanged = lastSearchRef.current !== searchValue
      if (!list || searchChanged) {
        lastSearchRef.current = searchValue
        executeQuery({ variables: { filter: { parentWorkOrderId: listItem._id, search: searchValue } } })
      }
    }
  }, [isExpanded, toggleExpanded, list, searchValue, executeQuery, listItem._id])

  const rowProps = {
    ...listItem,
    _highlightClassName: highlightClassName,
    _index: index,
    _renderDataBodyCell: renderDataBodyCell,
    _key: key,
    _fields: null,
  } as BodyRowConfig<WorkOrder>

  return (
    <>
      <tr className={highlightClassName} onClick={onRowClick} onDoubleClick={onRowDoubleClick}>
        <ListRow {...rowProps} level={level} isExpanded={isExpanded} onToggleExpand={handleToggleExpand} />
      </tr>

      <ShowIf condition={isExpanded && loading}>
        <tr>
          <td colSpan={100} className="text-center py-2">
            <Spinner size="sm" animation="border" /> Loading...
          </td>
        </tr>
      </ShowIf>

      <ShowIf condition={isExpanded && !loading}>
        {(list ?? []).map((child, childIndex) => (
          <ExpandableWorkOrderRow
            key={child._id}
            listItem={child}
            index={childIndex}
            level={level + 1}
            searchValue={searchValue}
          />
        ))}
      </ShowIf>
    </>
  )
}
